import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetFormSubmissionById } from "../../services/react-query/form_submissions/useGetFormSubmissionById";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { Loading } from "../../uikit";
import _ from "lodash";
import { FormSubmission, FormSubmissionField } from "../../types/FormSubmission";
import FieldInput from "../../components/common/FieldsSection/FieldInput";
import { FieldValue } from "../../types/FieldCommon";
import { AxiosError } from "axios";
import { humanizeDate } from "../../utility/dateUtils";
import { useUpdateFormSubmission } from "../../services/react-query/form_submissions/useUpdateFormSubmission";
import FormSubmissionAttachments from "./FormSubmissionAttachments";
import { AttachmentOwnerType } from "../../types/Attachment";
import ConfirmationModal from "../../components/ConfirmationModal";

export default function FormAuthorize() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [formSubmission, setFormSubmission] = useState<FormSubmission | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);
  const { data: formSubmissionData, error: fetchError } = useGetFormSubmissionById({ uuid });
  const { mutate: updateFormSubmission } = useUpdateFormSubmission({ uuid });

  useEffect(() => {
    if (!formSubmissionData) return;
    setFormSubmission(formSubmissionData);
  }, [formSubmissionData]);

  const errorStatus = (fetchError as AxiosError | null)?.response?.status;
  if (errorStatus === 403) {
    navigate(`/form/authenticate/${uuid}`);
  } else if (errorStatus === 307) {
    console.log(fetchError);
    // this happens when verification code has expired
    const errorData = (fetchError as AxiosError<{ uuid: string }>).response?.data;
    navigate(`/form/authorize/${errorData?.uuid}`);
  }

  if (!formSubmission) return <Loading />;

  const handleFieldValueUpdate = (fieldUuid: string, value: FieldValue) => setFormSubmission({
    ...formSubmission,
    fields: _.map(formSubmission.fields, (field) => (field.uuid === fieldUuid ? { ...field, value } : field)),
  });

  const handleSave = () => {
    updateFormSubmission(formSubmission);
  };

  const handleSubmit = () => {
    updateFormSubmission({ ...formSubmission, submit: true });
    setConfirmationOpen(false);
    setTimeout(() => { window.location.href = "https://schedulink.io"; }, 2000);
  };

  const renderField = (field: FormSubmissionField) => (
    <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
      <Typography sx={{ width: { xs: '100%', sm: '30%' } }}>{field.label}</Typography>
      <FieldInput field={field} showLabel={false} updateFieldValue={(value) => handleFieldValueUpdate(field.uuid, value)} />
    </Box>
  );
  const eventFormTemplate = formSubmissionData?.event_form_template;

  return (
    <Container component="main" maxWidth="lg">
      <title>ScheduLink | Studio Management Software for Volume Photo Studios</title>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h2">{eventFormTemplate?.organization_name}</Typography>
        <Typography variant="h1">{eventFormTemplate?.form_template_name}</Typography>
        <Typography variant="h2">Due: {humanizeDate(eventFormTemplate?.close_date)}</Typography>
      </Box>
      <Box sx={{ my: 3 }}>
        <Typography>
          Please provide the requested information and upload any necessary files below.
          You can submit your form now, or save your progress to finish later.
        </Typography>
      </Box>
      <Stack gap={1}>
        {
          _.map(formSubmission.fields, renderField)
        }
      </Stack>

      <FormSubmissionAttachments ownerType={AttachmentOwnerType.FORM_TEMPLATE} ownerUuid={formSubmission.event_form_template.form_template_uuid} />
      <FormSubmissionAttachments ownerType={AttachmentOwnerType.FORM_SUBMISSION} ownerUuid={formSubmission.uuid} />

      <Box sx={{ display: 'flex', gap: 20, justifyContent: 'center', mb: 5 }}>
        <Button variant="outlined" onClick={handleSave}>Save</Button>
        <Button variant="contained" onClick={() => setConfirmationOpen(true)}>Submit</Button>
      </Box>

      <ConfirmationModal
        text="Once the form is submitted it cannot be modified without contacting the studio. Are you sure you want to submit this form?"
        isOpen={confirmationOpen}
        onConfirm={handleSubmit}
        onCancel={() => setConfirmationOpen(false)}
      />
    </Container>
  );
}